import { fetchTrack, type TrackSample } from "../export/track";
import type { FlightSetDetail } from "../api/types";

export interface GhostTrack {
  flightId: string;
  name: string;
  latlngs: [number, number][];
}

/** Thin a track to ~one point per `intervalS` seconds of video time. Ghost
 *  polylines are context, not playback surfaces — 24 Hz would put tens of
 *  thousands of points into the SVG renderer per sibling. Always keeps the
 *  final sample so the track visibly reaches its endpoint. */
export function decimateSamples(
  samples: TrackSample[],
  intervalS = 1,
): [number, number][] {
  const out: [number, number][] = [];
  let nextT = -Infinity;
  for (const s of samples) {
    if (s.t >= nextT) {
      out.push([s.lat, s.lon]);
      nextT = s.t + intervalS;
    }
  }
  const last = samples[samples.length - 1];
  if (last !== undefined && out.length > 0) {
    const tail = out[out.length - 1]!;
    if (tail[0] !== last.lat || tail[1] !== last.lon) {
      out.push([last.lat, last.lon]);
    }
  }
  return out;
}

/** Fetch + decimate every sibling member's track. Per-sibling failures are
 *  logged and skipped — a ghost simply doesn't appear (spec §5.1). */
export async function loadGhostTracks(
  set: FlightSetDetail,
  currentFlightId: string,
  fetchTrackFn: (url: string) => Promise<TrackSample[]> = fetchTrack,
): Promise<GhostTrack[]> {
  const siblings = set.members.filter((m) => m.flightId !== currentFlightId);
  const results = await Promise.all(
    siblings.map(async (m): Promise<GhostTrack | null> => {
      try {
        const samples = await fetchTrackFn(m.trackUrl);
        const latlngs = decimateSamples(samples);
        return latlngs.length >= 2
          ? { flightId: m.flightId, name: m.name, latlngs }
          : null;
      } catch (err) {
        console.warn("ghost track fetch failed", m.flightId, err);
        return null;
      }
    }),
  );
  return results.filter((g): g is GhostTrack => g !== null);
}
